import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ToastKind = "success" | "error" | "info";

interface ToastItem {
  id: number;
  msg: string;
  kind: ToastKind;
}

interface ToastCtx {
  toast: (msg: string, kind?: ToastKind) => void;
}

const Ctx = createContext<ToastCtx>(null as unknown as ToastCtx);

export const useToast = () => useContext(Ctx);

// Module-level sink so non-React code (e.g. the api client) can push toasts.
type ToastSink = (msg: string, kind: ToastKind) => void;
let sink: ToastSink | null = null;

export function setToastSink(fn: ToastSink | null) {
  sink = fn;
}

export function emitToast(msg: string, kind: ToastKind = "info") {
  sink?.(msg, kind);
}

const DISMISS_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (msg: string, kind: ToastKind = "info") => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, msg, kind }]);
      window.setTimeout(() => dismiss(id), DISMISS_MS);
    },
    [dismiss],
  );

  // Register the dispatcher so non-React code can emit toasts.
  useEffect(() => {
    setToastSink(toast);
    return () => setToastSink(null);
  }, [toast]);

  return (
    <Ctx.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((t) => (
          <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const tones: Record<ToastKind, string> = {
    error: "border-red-500/40 bg-red-500/15 text-red-100",
    success: "border-emerald-500/40 bg-emerald-500/15 text-emerald-100",
    info: "border-white/15 bg-slate-700/80 text-white",
  };
  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg backdrop-blur ${tones[item.kind]}`}
    >
      <span className="flex-1 break-words">{item.msg}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-1 -mt-0.5 shrink-0 rounded p-1 text-white/60 transition hover:text-white"
      >
        ✕
      </button>
    </div>
  );
}
